import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../../api/client";
import Tabs from "../../components/Tabs";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import { usePermission } from "../../../../hooks/usePermission";
import { PERMISSIONS } from "../../../../constants/permissions";
import MachineModal from "./MachineModal";
import MaintenanceModal from "./MaintenanceModal";
import ScheduleModal from "./ScheduleModal";
import { ReportBreakdownModal, ResolveBreakdownModal } from "./BreakdownModals";
import {
  BREAKDOWN_STATUS,
  MACHINE_STATUS,
  PM_STATE,
  RECORD_TYPES,
  WARRANTY_STATE,
  errorMessage,
  fileUrl,
  formatDate,
  formatDuration,
  formatHours,
  formatMoney,
  formatPercent,
  formatWhen,
  intervalText,
  machineLabel,
  pmDueText,
} from "./assetUtils";
import "./Assets.css";

const P = PERMISSIONS;
const TAB_KEYS = ["overview", "machines", "breakdowns", "maintenance", "reliability"];
const PERIODS = [7, 30, 90, 365];

export function Pill({ meta }) {
  if (!meta) return null;
  return <span className={`am-pill ${meta.tone}`}>{meta.label}</span>;
}

function MachineIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 21h18M5 21V9l5 3V9l5 3V5h4v16" />
      <path d="M9 17h1M14 17h1" />
    </svg>
  );
}

export function MachineThumb({ machine }) {
  return machine.imagePath ? <img src={fileUrl(machine.imagePath)} alt="" loading="lazy" /> : <MachineIcon />;
}

// --- tabs ----------------------------------------------------------------------

function Overview({ data, onOpenBreakdown, canMaintain, onResolve, onAcknowledge, onCompletePm }) {
  const { machines, openBreakdowns, pmDue, pmDueSoon, warranties, reliability } = data;
  return (
    <>
      <div className="am-stats">
        <div className="am-stat">
          <p className="am-stat-label">Machines</p>
          <p className="am-stat-value">{machines.total - machines.retired}</p>
          <p className="am-stat-note">{machines.running} running · {machines.maintenance} in maintenance</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">Down right now</p>
          <p className={`am-stat-value${machines.down ? " bad" : ""}`}>{machines.down}</p>
          <p className="am-stat-note">{openBreakdowns.length} open breakdown{openBreakdowns.length === 1 ? "" : "s"}</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">PM due / overdue</p>
          <p className={`am-stat-value${pmDue.length ? " warn" : ""}`}>{pmDue.length}</p>
          <p className="am-stat-note">{pmDueSoon.length} due soon</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">Availability · 30 days</p>
          <p className="am-stat-value">{formatPercent(reliability.availability)}</p>
          <p className="am-stat-note">{formatHours(reliability.downtimeHours)} downtime</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">MTTR · MTBF</p>
          <p className="am-stat-value" style={{ fontSize: 18 }}>
            {formatHours(reliability.mttrHours)} · {formatHours(reliability.mtbfHours)}
          </p>
          <p className="am-stat-note">{reliability.failures} breakdown{reliability.failures === 1 ? "" : "s"} in 30 days</p>
        </div>
      </div>

      <div className="am-panel">
        <div className="am-panel-head">
          <h2 className="am-panel-title">Open breakdowns</h2>
        </div>
        {openBreakdowns.length === 0 ? (
          <p className="am-muted" style={{ margin: 0 }}>Every machine is running. 🎉</p>
        ) : (
          <BreakdownTable rows={openBreakdowns} onOpen={onOpenBreakdown} canMaintain={canMaintain} onResolve={onResolve} onAcknowledge={onAcknowledge} />
        )}
      </div>

      <div className="am-grid-2">
        <div className="am-panel">
          <div className="am-panel-head">
            <h2 className="am-panel-title">Preventive maintenance due</h2>
          </div>
          {pmDue.length + pmDueSoon.length === 0 ? (
            <p className="am-muted" style={{ margin: 0 }}>Nothing due in the coming days.</p>
          ) : (
            <ScheduleTable rows={[...pmDue, ...pmDueSoon]} compact canMaintain={canMaintain} onComplete={onCompletePm} />
          )}
        </div>
        <div className="am-panel">
          <div className="am-panel-head">
            <h2 className="am-panel-title">Warranties to watch</h2>
          </div>
          {warranties.length === 0 ? (
            <p className="am-muted" style={{ margin: 0 }}>No warranty or AMC ends in the next 30 days.</p>
          ) : (
            <div className="am-table-wrap">
              <table className="am-table">
                <tbody>
                  {warranties.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <Link to={`/manufacturing/assets/machines/${w.machine.id}`} className="am-strong">{machineLabel(w.machine)}</Link>
                        <div className="am-muted">{w.provider}</div>
                      </td>
                      <td>{formatDate(w.endDate)}</td>
                      <td><Pill meta={WARRANTY_STATE[w.state]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function MachinesTab({ machines }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const types = useMemo(() => [...new Set(machines.map((m) => m.type))].sort(), [machines]);
  const shown = machines.filter((m) => {
    if (status ? m.status !== status : m.status === "retired") return false;
    if (type && m.type !== type) return false;
    if (!q) return true;
    const hay = `${m.code} ${m.name} ${m.serialNumber || ""} ${m.make || ""} ${m.model || ""} ${m.location || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  return (
    <>
      <div className="am-filters">
        <input className="am-input" placeholder="Search code, name, serial, location…" value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} aria-label="Search machines" />
        <select className="am-select" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Filter by status">
          <option value="">Active machines</option>
          {Object.entries(MACHINE_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {types.length > 1 && (
          <select className="am-select" value={type} onChange={(e) => setType(e.target.value)} aria-label="Filter by type">
            <option value="">All types</option>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>
      {shown.length === 0 ? (
        <div className="am-empty"><strong>No machines match</strong>Try a different search or filter.</div>
      ) : (
        <div className="am-machine-grid">
          {shown.map((m) => (
            <Link key={m.id} to={`/manufacturing/assets/machines/${m.id}`} className={`am-machine-card${m.status === "down" ? " down" : ""}`}>
              <div className="am-machine-thumb"><MachineThumb machine={m} /></div>
              <div className="am-machine-body">
                <div className="am-machine-top">
                  <p className="am-machine-name">{m.name}</p>
                  <Pill meta={MACHINE_STATUS[m.status]} />
                </div>
                <div className="am-machine-meta">
                  <span className="am-code">{m.code}</span> · {m.type}{m.location ? ` · ${m.location}` : ""}
                </div>
                {m.serialNumber && <div className="am-machine-meta">S/N {m.serialNumber}</div>}
                {m.openBreakdowns?.length > 0 && (
                  <div className="am-machine-alert">Down since {formatWhen(m.openBreakdowns[0].startedAt)}</div>
                )}
                {["expiring", "expired"].includes(m.warrantyState) && (
                  <div className="am-machine-meta">Warranty {m.warrantyState === "expired" ? "ended" : "ends"} {formatDate(m.warrantyEndsAt)}</div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

export function BreakdownTable({ rows, onOpen, canMaintain, onResolve, onAcknowledge, hideMachine }) {
  return (
    <div className="am-table-wrap">
      <table className="am-table">
        <thead>
          <tr>
            <th>#</th>
            {!hideMachine && <th>Machine</th>}
            <th>What happened</th>
            <th>Reported</th>
            <th>Status</th>
            <th className="am-num">Downtime</th>
            {canMaintain && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} className="am-row-link" onClick={() => onOpen(b)}>
              <td className="am-strong">{b.label}</td>
              {!hideMachine && <td>{machineLabel(b.machine)}</td>}
              <td><div className="am-clip">{b.description}</div>{b.voicePath && <span className="am-muted">🎤 voice note</span>}</td>
              <td>
                {formatWhen(b.startedAt)}
                <div className="am-muted">{b.reportedBy?.name || "—"} · {b.reportedVia === "web" ? "app" : b.reportedVia}</div>
              </td>
              <td><Pill meta={BREAKDOWN_STATUS[b.status]} /></td>
              <td className="am-num">{formatDuration(b.downtimeMinutes)}</td>
              {canMaintain && (
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: "nowrap" }}>
                  {b.status === "open" && (
                    <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => onAcknowledge(b)}>Acknowledge</button>
                  )}{" "}
                  {b.status !== "resolved" && (
                    <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={() => onResolve(b)}>Resolve</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ScheduleTable({ rows, compact, canMaintain, canManage, onComplete, onEdit, onDelete, hideMachine }) {
  return (
    <div className="am-table-wrap">
      <table className="am-table">
        <thead>
          <tr>
            <th>Maintenance</th>
            {!hideMachine && <th>Machine</th>}
            {!compact && <th>Interval</th>}
            <th>Next due</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>
                <span className="am-strong">{s.title}</span>
                {!s.isActive && <span className="am-muted"> · paused</span>}
                {s.checklist && <div className="am-muted">✓ {s.checklist.name}</div>}
              </td>
              {!hideMachine && (
                <td><Link to={`/manufacturing/assets/machines/${s.machine.id}`}>{machineLabel(s.machine)}</Link></td>
              )}
              {!compact && <td>{intervalText(s)}<div className="am-muted">{s.assignee ? s.assignee.name : "Machine's technician"}</div></td>}
              <td>{pmDueText(s)}</td>
              <td><Pill meta={PM_STATE[s.due.state]} /></td>
              <td style={{ whiteSpace: "nowrap" }}>
                {canMaintain && s.isActive && (
                  <button type="button" className="am-btn am-btn-primary am-btn-sm" onClick={() => onComplete(s)}>Complete</button>
                )}
                {canManage && onEdit && (
                  <>
                    {" "}
                    <button type="button" className="am-link-btn" onClick={() => onEdit(s)}>Edit</button>
                    {" · "}
                    <button type="button" className="am-link-btn danger" onClick={() => onDelete(s)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function RecordTable({ rows, hideMachine }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="am-table-wrap">
      <table className="am-table">
        <thead>
          <tr>
            <th>Date</th>
            {!hideMachine && <th>Machine</th>}
            <th>Type</th>
            <th>Work done</th>
            <th>By</th>
            <th className="am-num">Downtime</th>
            <th className="am-num">Cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <React.Fragment key={r.id}>
              <tr className="am-row-link" onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id}>
                <td>{formatWhen(r.performedAt)}</td>
                {!hideMachine && <td>{machineLabel(r.machine)}</td>}
                <td>
                  {RECORD_TYPES.find((t) => t.value === r.type)?.label || r.type}
                  {r.schedule && <div className="am-muted">{r.schedule.title}</div>}
                </td>
                <td>
                  <div className="am-clip">{r.description}</div>
                  {r.checklistName && (
                    <span className="am-muted">✓ {r.checklistName}{r.issuesFound ? <span style={{ color: "#b91c1c", fontWeight: 700 }}> · {r.issuesFound} not OK</span> : ""}</span>
                  )}
                </td>
                <td>{r.performedByLabel || "—"}</td>
                <td className="am-num">{r.downtimeMinutes != null ? formatDuration(r.downtimeMinutes) : "—"}</td>
                <td className="am-num">{formatMoney(r.cost)}</td>
              </tr>
              {open === r.id && (
                <tr>
                  <td colSpan={hideMachine ? 6 : 7} style={{ background: "#f8fafc" }}>
                    <p style={{ whiteSpace: "pre-wrap", margin: "0 0 8px", fontSize: 13 }}>{r.description}</p>
                    {r.meterReading != null && <p className="am-muted">Meter reading: {r.meterReading} h</p>}
                    {Array.isArray(r.checklist) && (
                      <ul className="am-filled-list">
                        {r.checklist.map((i) => (
                          <li key={i.id}>
                            <span>{i.label}{i.note ? <span className="am-muted"> — {i.note}</span> : null}</span>
                            <span className={i.value === "not_ok" ? "not_ok" : ""}>
                              {i.value == null ? "—" : i.type === "check" ? { ok: "OK", not_ok: "Not OK", na: "N/A" }[i.value] : String(i.value)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {r.attachmentPath && (
                      <a href={fileUrl(r.attachmentPath)} target="_blank" rel="noreferrer" className="am-link-btn">View attachment ↗</a>
                    )}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReliabilityView({ data }) {
  const max = Math.max(...data.machines.map((r) => r.downtimeHours), 0);
  return (
    <>
      <div className="am-stats">
        <div className="am-stat">
          <p className="am-stat-label">Availability</p>
          <p className="am-stat-value">{formatPercent(data.overall.availability)}</p>
          <p className="am-stat-note">Share of time machines could run</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">Downtime</p>
          <p className="am-stat-value">{formatHours(data.overall.downtimeHours)}</p>
          <p className="am-stat-note">{data.overall.failures} breakdown{data.overall.failures === 1 ? "" : "s"}</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">MTTR</p>
          <p className="am-stat-value">{formatHours(data.overall.mttrHours)}</p>
          <p className="am-stat-note">Mean time to repair</p>
        </div>
        <div className="am-stat">
          <p className="am-stat-label">MTBF</p>
          <p className="am-stat-value">{formatHours(data.overall.mtbfHours)}</p>
          <p className="am-stat-note">Mean time between failures</p>
        </div>
      </div>

      <div className="am-panel">
        <div className="am-panel-head">
          <h2 className="am-panel-title">Downtime by machine</h2>
        </div>
        {max === 0 ? (
          <p className="am-muted" style={{ margin: 0 }}>No downtime in this period.</p>
        ) : (
          <div className="am-bars">
            {data.machines.filter((r) => r.downtimeHours > 0).map((r) => (
              <div className="am-bar-row" key={r.machine.id}>
                <Link to={`/manufacturing/assets/machines/${r.machine.id}`} className="am-strong" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {machineLabel(r.machine)}
                </Link>
                <div className="am-bar-track" title={formatHours(r.downtimeHours)}>
                  <div className="am-bar-fill" style={{ width: `${(r.downtimeHours / max) * 100}%` }} />
                </div>
                <span className="am-num">{formatHours(r.downtimeHours)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="am-table-wrap">
        <table className="am-table">
          <thead>
            <tr>
              <th>Machine</th>
              <th className="am-num">Breakdowns</th>
              <th className="am-num">Downtime</th>
              <th className="am-num">Availability</th>
              <th className="am-num">MTTR</th>
              <th className="am-num">MTBF</th>
            </tr>
          </thead>
          <tbody>
            {data.machines.map((r) => (
              <tr key={r.machine.id}>
                <td><Link to={`/manufacturing/assets/machines/${r.machine.id}`}>{machineLabel(r.machine)}</Link></td>
                <td className="am-num">{r.failures}</td>
                <td className="am-num">{formatHours(r.downtimeHours)}</td>
                <td className="am-num">{formatPercent(r.availability)}</td>
                <td className="am-num">{formatHours(r.mttrHours)}</td>
                <td className="am-num">{formatHours(r.mtbfHours)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="am-muted">
        Measured round the clock from each machine's installation date (or the start of the period). Downtime counts from when a machine stopped to when its breakdown was resolved.
      </p>
    </>
  );
}

// --- page ----------------------------------------------------------------------

export default function AssetManagement() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TAB_KEYS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const setTab = (key) => setParams(key === "overview" ? {} : { tab: key }, { replace: true });

  const canManage = usePermission(P.MANUFACTURING_ASSET_MANAGE);
  const canMaintain = usePermission([P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);
  const canReport = usePermission([P.MANUFACTURING_ASSET_BREAKDOWN_REPORT, P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);
  const canSeeMaintenance = usePermission([P.MANUFACTURING_ASSET_VIEW, P.MANUFACTURING_ASSET_MAINTAIN, P.MANUFACTURING_ASSET_MANAGE]);

  const [overview, setOverview] = useState(null);
  const [machines, setMachines] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [records, setRecords] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [people, setPeople] = useState([]);
  const [reliability, setReliability] = useState(null);
  const [period, setPeriod] = useState(30);
  const [bdFilter, setBdFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  // { kind: "machine" | "report" | "resolve" | "maintenance" | "schedule", ... }
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const calls = [
        api.get("/manufacturing/assets/overview"),
        api.get("/manufacturing/assets/machines"),
        api.get("/manufacturing/assets/breakdowns", { params: { limit: 300 } }),
        api.get("/manufacturing/assets/people"),
      ];
      if (canSeeMaintenance) {
        calls.push(
          api.get("/manufacturing/assets/pm-schedules"),
          api.get("/manufacturing/assets/maintenance", { params: { limit: 300 } }),
          api.get("/manufacturing/assets/checklists"),
        );
      }
      const [ov, ms, bds, ppl, sch, rec, cl] = await Promise.all(calls);
      setOverview(ov.data.data);
      setMachines(ms.data.data);
      setBreakdowns(bds.data.data);
      setPeople(ppl.data.data);
      if (sch) {
        setSchedules(sch.data.data);
        setRecords(rec.data.data);
        setChecklists(cl.data.data);
      }
    } catch (err) {
      setError(errorMessage(err, "Couldn't load Asset Management."));
    } finally {
      setLoading(false);
    }
  }, [canSeeMaintenance]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== "reliability" || !canSeeMaintenance) return;
    api.get("/manufacturing/assets/reliability", { params: { days: period } })
      .then((res) => setReliability(res.data.data))
      .catch((err) => setToast({ msg: errorMessage(err, "Couldn't load reliability figures."), type: "error" }));
  }, [tab, period, canSeeMaintenance]);

  const saved = (msg) => () => {
    setModal(null);
    setToast({ msg, type: "success" });
    load();
  };

  const acknowledge = async (b) => {
    try {
      await api.post(`/manufacturing/assets/breakdowns/${b.id}/acknowledge`);
      setToast({ msg: `${b.label} acknowledged`, type: "success" });
      load();
    } catch (err) {
      setToast({ msg: errorMessage(err, "Couldn't acknowledge."), type: "error" });
    }
  };

  const [confirmSchedule, setConfirmSchedule] = useState(null);
  const deleteSchedule = async (s) => {
    setConfirmSchedule(null);
    try {
      await api.delete(`/manufacturing/assets/pm-schedules/${s.id}`);
      setToast({ msg: "Schedule deleted", type: "success" });
      load();
    } catch (err) {
      setToast({ msg: errorMessage(err, "Couldn't delete the schedule."), type: "error" });
    }
  };

  const openBreakdown = (b) => navigate(`/manufacturing/assets/machines/${b.machineId}?breakdown=${b.id}`);

  const shownBreakdowns = breakdowns.filter((b) => (bdFilter === "open" ? b.status !== "resolved" : bdFilter ? b.status === bdFilter : true));

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "machines", label: `Machines (${machines.filter((m) => m.status !== "retired").length})` },
    { key: "breakdowns", label: `Breakdowns${overview?.openBreakdowns.length ? ` (${overview.openBreakdowns.length} open)` : ""}` },
    ...(canSeeMaintenance
      ? [
        { key: "maintenance", label: "Maintenance" },
        { key: "reliability", label: "Downtime & reliability" },
      ]
      : []),
  ];

  return (
    <div className="am-page">
      <Link className="back-link" to="/manufacturing/dashboard">← Back to Dashboard</Link>

      <div className="am-head">
        <div>
          <h1 className="am-title">Asset Management</h1>
          <p className="am-sub">Every machine, its warranty and service history, planned maintenance, and the breakdowns that stop production.</p>
        </div>
        <div className="am-actions">
          {canReport && (
            <button type="button" className="am-btn am-btn-danger" onClick={() => setModal({ kind: "report" })} disabled={machines.length === 0}>
              🚨 Report breakdown
            </button>
          )}
          {canMaintain && (
            <button type="button" className="am-btn am-btn-ghost" onClick={() => setModal({ kind: "maintenance" })} disabled={machines.length === 0}>
              Log maintenance
            </button>
          )}
          {canManage && (
            <button type="button" className="am-btn am-btn-primary" onClick={() => setModal({ kind: "machine" })}>+ Add machine</button>
          )}
        </div>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div className="am-empty">Loading…</div>
      ) : error ? (
        <div className="am-empty"><strong>Something went wrong</strong>{error}</div>
      ) : machines.length === 0 ? (
        <div className="am-empty">
          <strong>No machines yet</strong>
          {canManage ? "Add your first machine to start tracking its maintenance and breakdowns." : "Ask your administrator to add your machines."}
        </div>
      ) : tab === "overview" ? (
        <Overview
          data={overview}
          onOpenBreakdown={openBreakdown}
          canMaintain={canMaintain}
          onResolve={(b) => setModal({ kind: "resolve", breakdown: b })}
          onAcknowledge={acknowledge}
          onCompletePm={(s) => setModal({ kind: "maintenance", schedule: s })}
        />
      ) : tab === "machines" ? (
        <MachinesTab machines={machines} />
      ) : tab === "breakdowns" ? (
        <>
          <div className="am-filters">
            <select className="am-select" value={bdFilter} onChange={(e) => setBdFilter(e.target.value)} aria-label="Filter breakdowns">
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="">All</option>
            </select>
          </div>
          {shownBreakdowns.length === 0 ? (
            <div className="am-empty"><strong>No breakdowns here</strong>{bdFilter === "open" ? "Every machine is running." : "Nothing has been reported yet."}</div>
          ) : (
            <BreakdownTable
              rows={shownBreakdowns}
              onOpen={openBreakdown}
              canMaintain={canMaintain}
              onResolve={(b) => setModal({ kind: "resolve", breakdown: b })}
              onAcknowledge={acknowledge}
            />
          )}
        </>
      ) : tab === "maintenance" ? (
        <>
          <div className="am-panel">
            <div className="am-panel-head">
              <h2 className="am-panel-title">Preventive maintenance schedules</h2>
              {canManage && (
                <button type="button" className="am-btn am-btn-ghost am-btn-sm" onClick={() => setModal({ kind: "schedule" })}>+ New schedule</button>
              )}
            </div>
            {schedules.length === 0 ? (
              <p className="am-muted" style={{ margin: 0 }}>
                No schedules yet. {canManage ? "Set one up per machine — every N days or N running hours — with a checklist to fill in." : ""}
              </p>
            ) : (
              <ScheduleTable
                rows={schedules}
                canMaintain={canMaintain}
                canManage={canManage}
                onComplete={(s) => setModal({ kind: "maintenance", schedule: s })}
                onEdit={(s) => setModal({ kind: "schedule", schedule: s })}
                onDelete={setConfirmSchedule}
              />
            )}
          </div>
          <div className="am-panel">
            <div className="am-panel-head">
              <h2 className="am-panel-title">Maintenance history</h2>
            </div>
            {records.length === 0 ? (
              <p className="am-muted" style={{ margin: 0 }}>Nothing logged yet.</p>
            ) : (
              <RecordTable rows={records} />
            )}
          </div>
        </>
      ) : tab === "reliability" ? (
        <>
          <div className="am-filters" style={{ justifyContent: "flex-end" }}>
            <div className="am-period" role="group" aria-label="Period">
              {PERIODS.map((d) => (
                <button key={d} type="button" className={period === d ? "active" : ""} onClick={() => setPeriod(d)}>
                  {d === 365 ? "1 year" : `${d} days`}
                </button>
              ))}
            </div>
          </div>
          {reliability ? <ReliabilityView data={reliability} /> : <div className="am-empty">Loading…</div>}
        </>
      ) : null}

      {modal?.kind === "machine" && (
        <MachineModal people={people} onClose={() => setModal(null)} onSaved={(m) => { setModal(null); navigate(`/manufacturing/assets/machines/${m.id}`); }} />
      )}
      {modal?.kind === "report" && (
        <ReportBreakdownModal
          machines={machines}
          onClose={() => setModal(null)}
          onSaved={(res) => saved(res.duplicate ? `Added to ${res.breakdown.label}` : res.notified ? `${res.breakdown.label} reported — the technician has been alerted on chat` : `${res.breakdown.label} reported — but no technician could be alerted on chat`)()}
        />
      )}
      {modal?.kind === "resolve" && (
        <ResolveBreakdownModal breakdown={modal.breakdown} onClose={() => setModal(null)} onSaved={saved(`${modal.breakdown.label} resolved`)} />
      )}
      {modal?.kind === "maintenance" && (
        <MaintenanceModal
          machines={machines}
          schedule={modal.schedule}
          checklists={checklists}
          people={people}
          onClose={() => setModal(null)}
          onSaved={saved(modal.schedule ? "Preventive maintenance completed" : "Maintenance logged")}
        />
      )}
      {modal?.kind === "schedule" && (
        <ScheduleModal
          schedule={modal.schedule}
          machines={machines}
          checklists={checklists}
          people={people}
          onClose={() => setModal(null)}
          onSaved={saved(modal.schedule ? "Schedule updated" : "Schedule created")}
        />
      )}
      {confirmSchedule && (
        <ConfirmDialog
          title={`Delete "${confirmSchedule.title}"?`}
          message="Reminders stop. Maintenance already logged against it is kept."
          confirmLabel="Delete schedule"
          confirmClass="btn-danger"
          onClose={() => setConfirmSchedule(null)}
          onConfirm={() => deleteSchedule(confirmSchedule)}
        />
      )}
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
